import React, { useState, useEffect } from "react";
import {
  fetchdiscount,
  changeDiscountStatus,
  deleteDiscount,
} from "../../services/discountService";
import { Icon } from "@iconify/react";
import { useNavigate, useSearchParams } from "react-router-dom";
import SuccessAlert from "../../components/child/Alert/SuccessAlert";
import DengerAlert from "../../components/child/Alert/DengerAlert";
import { showConfirmationDialog } from "../../utils/apiHelper";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Link } from "react-router-dom";

const DiscountList = () => {
  const [items, setItems] = useState([]);
  const [sortColumn, setSortColumn] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [searchQuery, setSearchQuery] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedItems, setSelectedItems] = useState([]);

  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [startDate, setStartDate] = useState(null); // keep Date object
  const [endDate, setEndDate] = useState(null); // keep Date object

  const navigate = useNavigate();

  const fetchData = async () => {
    try {
      const formattedStartDate = startDate
        ? moment(startDate).format("YYYY-MM-DD")
        : "";
      const formattedEndDate = endDate
        ? moment(endDate).format("YYYY-MM-DD")
        : "";

      // ✅ Validation: start date <= end date
      if (
        formattedStartDate &&
        formattedEndDate &&
        moment(formattedStartDate).isAfter(moment(formattedEndDate))
      ) {
        setErrorMessage("Start date cannot be greater than end date");
        setTimeout(() => setErrorMessage(""), 3000);
        return;
      }

      setLoading(true);
      const res = await fetchdiscount({
        page: currentPage,
        limit: itemsPerPage,
        search: searchQuery,
        sortBy: sortColumn,
        sortOrder,
        status: statusFilter,
        type: typeFilter,
        startDate: formattedStartDate, // ✅ lowercase
        endDate: formattedEndDate,
      });

      setItems(res.data.data);
      setTotalItems(res.data.pagination.total);
      setTotalPages(res.data.pagination.totalPages);
    } catch (error) {
      setErrorMessage("Failed to fetch discounts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [
    searchQuery,
    currentPage,
    itemsPerPage,
    sortColumn,
    sortOrder,
    statusFilter,
    typeFilter,
    startDate,
    endDate,
  ]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortOrder("asc");
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedItems(items.map((item) => item._id));
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectItem = (id) => {
    setSelectedItems((prevSelected) => {
      if (prevSelected.includes(id)) {
        return prevSelected.filter((item) => item !== id);
      } else {
        return [...prevSelected, id];
      }
    });
  };

  const handleStatusClick = async (id, currentStatus) => {
    const newStatus = currentStatus === "active" ? "inactive" : "active";
    const result = await showConfirmationDialog(
      `This action will change the status to ${newStatus}.`,
      "Yes, change it!"
    );
    if (!result.isConfirmed) return;

    try {
      const response = await changeDiscountStatus(id, { status: newStatus });
      setSuccessMessage(
        response?.data?.message ||
          response?.message ||
          "Status updated successfully"
      );
      fetchData();
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      setErrorMessage(
        err?.response?.data?.message || err?.message || "Error updating status"
      );
      setTimeout(() => setErrorMessage(""), 3000);
    }
  };

  const isSelected = (id) => selectedItems.includes(id);

  const handleDeleteAll = async (id, single = "No") => {
    const idsToDelete = single === "Yes" ? [id] : selectedItems;
    if (idsToDelete.length === 0) return;

    const result = await showConfirmationDialog(
      "This action will delete the discount(s)!",
      "Yes, delete it!"
    );
    if (!result.isConfirmed) return;

    try {
      console.log(idsToDelete);
      await deleteDiscount({ id: idsToDelete }); // call delete API here
      setSuccessMessage(
        single === "Yes"
          ? "Discount deleted successfully"
          : "Discounts deleted successfully"
      );
      if (single === "No") setSelectedItems([]);
      fetchData(currentPage);
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      setErrorMessage(error.message);
      setTimeout(() => setErrorMessage(""), 3000);
    }
  };

  const getPaginationRange = () => {
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  };

  return (
    <div className="card h-100 p-0 radius-12">
      {successMessage && (
        <SuccessAlert
          message={successMessage}
          onClose={() => setSuccessMessage("")}
        />
      )}
      {errorMessage && (
        <DengerAlert
          message={errorMessage}
          onClose={() => setErrorMessage("")}
        />
      )}

      {/* Header and Filters */}
      <div className="card-header border-bottom bg-base px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
        <div className="d-flex align-items-center flex-wrap gap-3">
          {/* Page size */}
          <select
            className="form-select form-select-sm w-auto ps-12"
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(parseInt(e.target.value));
              setCurrentPage(1);
            }}
          >
            <option value="10">10 items per page</option>
            <option value="20">20 items per page</option>
            <option value="50">50 items per page</option>
            <option value="100">100 items per page</option>
          </select>

          {/* Search */}
          <form className="navbar-search" style={{ position: "relative" }}>
            <input
              type="text"
              className="bg-base h-40-px w-auto pe-40"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyUp={(e) => {
                if (e.target.value === "") setCurrentPage(1);
              }}
              style={{
                paddingRight: searchQuery ? "5rem" : "2.5rem",
              }}
            />
            <Icon
              icon="ion:search-outline"
              className="icon"
              style={{
                position: "absolute",
                right: searchQuery ? "3.25rem" : "1rem",
                top: "50%",
                transform: "translateY(-50%)",
                fontSize: "1.25rem",
                color: "#999",
                pointerEvents: "none",
              }}
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => {
                  setSearchQuery("");
                  setCurrentPage(1);
                }}
                style={{
                  position: "absolute",
                  right: "1rem",
                  top: "30%",
                  transform: "translateY(-50%)",
                  fontSize: "1.25rem",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                <Icon icon="ic:round-close" />
              </button>
            )}
          </form>

          {/* Filters */}
          <select
            className="form-select form-select-sm w-auto ps-12"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="expired">Expired</option>
          </select>

          <select
            className="form-select form-select-sm w-auto ps-12"
            value={typeFilter}
            onChange={(e) => {
              setTypeFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">All Type</option>
            <option value="admin">Admin</option>
            <option value="user">User</option>
          </select>

          {/* Date Filters */}
          <div style={{ position: "relative", maxWidth: "200px" }}>
            <DatePicker
              selected={startDate}
              onChange={(date) => {
                setStartDate(date);
                setEndDate(null); // reset endDate
                setCurrentPage(1);
              }}
              className="form-control"
              placeholderText="Start Date"
              dateFormat="yyyy-MM-dd"
            />
          </div>

          <div style={{ position: "relative", maxWidth: "200px" }}>
            <DatePicker
              selected={endDate}
              onChange={(date) => {
                setEndDate(date);
                setCurrentPage(1);
              }}
              className="form-control"
              placeholderText="End Date"
              dateFormat="yyyy-MM-dd"
              minDate={startDate}
            />
          </div>

          <button
            className="btn btn-danger"
            onClick={handleDeleteAll}
            disabled={selectedItems.length === 0}
          >
            Delete All
          </button>
          <Link
            to="/discount/add-discount"
            className="btn btn-primary text-sm btn-sm px-12  radius-8 d-flex align-items-center gap-2"
          >
            <Icon
              icon="ic:baseline-plus"
              className="icon text-xl line-height-1"
            />
            Add Discount
          </Link>
        </div>
      </div>

      {/* Table */}
      <div className="card-body p-24">
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table sm-table mb-0">
            <thead>
              <tr>
                <th>
                  <div className="form-check style-check d-flex align-items-center">
                    <input
                      className="form-check-input radius-4 border input-form-dark"
                      type="checkbox"
                      id="selectAll"
                      onChange={handleSelectAll}
                      checked={
                        selectedItems.length === items.length &&
                        items.length > 0
                      }
                    />
                  </div>
                </th>
                <th onClick={() => handleSort("code")}>Code</th>
                <th onClick={() => handleSort("discount")}>Discount</th>
                <th onClick={() => handleSort("type")}>Type</th>
                <th onClick={() => handleSort("startDate")}>Start Date</th>
                <th onClick={() => handleSort("endDate")}>End Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="text-center">
                    <span className="loader-15"></span>
                  </td>
                </tr>
              ) : items.length > 0 ? (
                items.map((cl) => (
                  <tr key={cl._id}>
                    <td>
                      <div className="form-check style-check d-flex align-items-center">
                        {cl.type === "admin" && (
                          <input
                            className="form-check-input radius-4 border"
                            type="checkbox"
                            value={cl._id}
                            checked={isSelected(cl._id)}
                            onChange={() => handleSelectItem(cl._id)}
                          />
                        )}
                      </div>
                    </td>
                    <td>{cl.code}</td>
                    <td>{cl.discount}</td>
                    <td>{cl.type}</td>
                    <td>{moment(cl.startDate).format("DD-MM-YYYY")}</td>
                    <td>{moment(cl.endDate).format("DD-MM-YYYY")}</td>
                    <td>
                      {(() => {
                        const today = moment();
                        const isExpired =
                          cl.endDate &&
                          moment(today).isAfter(moment(cl.endDate));
                        const displayStatus = isExpired ? "expired" : cl.status;

                        const baseClasses =
                          "px-24 py-4 radius-4 fw-medium text-sm border";

                        let statusClasses = "";
                        if (displayStatus === "active") {
                          statusClasses =
                            "bg-success-focus text-success-600 border-success-main cursor-pointer";
                        } else if (displayStatus === "inactive") {
                          statusClasses =
                            "bg-neutral-200 text-neutral-600 border-neutral-400 cursor-pointer";
                        } else if (displayStatus === "expired") {
                          statusClasses = "text-danger-600  cursor-default";
                        }

                        return (
                          <span
                            {...(!isExpired && {
                              onClick: () =>
                                handleStatusClick(cl._id, cl.status),
                            })}
                            className={`${baseClasses} ${statusClasses}`}
                          >
                            {displayStatus.charAt(0).toUpperCase() +
                              displayStatus.slice(1)}
                          </span>
                        );
                      })()}
                    </td>

                    <td>
                      <div className="d-flex align-items-center gap-10">
                        <button
                          type="button"
                          title="View"
                          onClick={() =>
                            navigate(`/discount/view-discount/${cl._id}`)
                          }
                          className="bg-info-focus bg-hover-info-200 text-info-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                        >
                          <Icon
                            icon="majesticons:eye-line"
                            className="icon text-xl"
                          />
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            navigate(`/discount/edit-discount/${cl._id}`)
                          }
                          title="Edit"
                          className="bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                        >
                          <Icon icon="lucide:edit" className="menu-icon" />
                        </button>
                        {cl.type === "admin" && (
                          <button
                            type="button"
                            title="Delete"
                            className="remove-item-btn bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                            onClick={() => handleDeleteAll(cl._id, "Yes")}
                          >
                            <Icon
                              icon="fluent:delete-24-regular"
                              className="menu-icon"
                            />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="text-center">
                    No Data Found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
          <span>
            {totalItems === 0 ? (
              <>No entry to show</>
            ) : (
              <>
                Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                {Math.min(currentPage * itemsPerPage, totalItems)} of{" "}
                {totalItems} entries
              </>
            )}
          </span>

          <ul className="pagination d-flex flex-wrap align-items-center gap-2 justify-content-center">
            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
              <button
                className="page-link"
                onClick={() =>
                  currentPage > 1 && setCurrentPage((prev) => prev - 1)
                }
              >
                <Icon icon="ep:d-arrow-left" />
              </button>
            </li>
            {getPaginationRange().map((pageNum) => (
              <li key={pageNum} className="page-item">
                <button
                  className={`page-link ${
                    currentPage === pageNum ? "bg-primary-600 text-white" : ""
                  }`}
                  onClick={() => setCurrentPage(pageNum)}
                >
                  {pageNum}
                </button>
              </li>
            ))}
            <li className="page-item">
              <button
                className="page-link"
                disabled={currentPage === totalPages}
                onClick={() =>
                  currentPage < totalPages && setCurrentPage(currentPage + 1)
                }
              >
                <Icon icon="ep:d-arrow-right" />
              </button>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default DiscountList;
